import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "./AuthContext";

const DashboardThemeContext = createContext(null);
const STORAGE_KEY = "dashboardTheme";

const defaultTheme = {
  theme_mode: "light",
  primary_color: "#2563EB",
  button_color: "#2563EB",
  button_text_color: "#FFFFFF",
  table_header_color: "#1E40AF",
  table_header_text_color: "#FFFFFF",
  sidebar_color: "#111827",
  sidebar_text_color: "#FFFFFF",
  use_default_colors: true,
};

const lightSystemColors = {
  dashboard_background: "#F3F4F6",
  card_background: "#FFFFFF",
  text_color: "#111827",
  muted_text_color: "#6B7280",
  border_color: "#E5E7EB",
  table_background: "#FFFFFF",
  input_background: "#FFFFFF",
};

const darkSystemColors = {
  dashboard_background: "#020617",
  card_background: "#0F172A",
  text_color: "#F9FAFB",
  muted_text_color: "#94A3B8",
  border_color: "#1E293B",
  table_background: "#0B1220",
  input_background: "#111827",
};

const normalizeTheme = (data = {}) => ({
  ...defaultTheme,
  theme_mode: data.theme_mode || defaultTheme.theme_mode,
  primary_color: data.primary_color || defaultTheme.primary_color,
  button_color: data.button_color || defaultTheme.button_color,
  button_text_color: data.button_text_color || defaultTheme.button_text_color,
  table_header_color: data.table_header_color || defaultTheme.table_header_color,
  table_header_text_color: data.table_header_text_color || defaultTheme.table_header_text_color,
  sidebar_color: data.sidebar_color || defaultTheme.sidebar_color,
  sidebar_text_color: data.sidebar_text_color || defaultTheme.sidebar_text_color,
  use_default_colors:
    typeof data.use_default_colors === "boolean"
      ? data.use_default_colors
      : defaultTheme.use_default_colors,
});

const getInitialTheme = () => {
  try {
    const savedTheme = localStorage.getItem(STORAGE_KEY);

    if (!savedTheme) {
      return defaultTheme;
    }

    return normalizeTheme(JSON.parse(savedTheme));
  } catch {
    return defaultTheme;
  }
};

const saveTheme = (theme) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(normalizeTheme(theme)));
  } catch {}
};

const removeTheme = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {}
};

const buildTheme = (theme) => {
  const systemColors = theme.theme_mode === "dark" ? darkSystemColors : lightSystemColors;

  return {
    ...theme,
    ...systemColors,
  };
};

export const DashboardThemeProvider = ({ children }) => {
  const { accessToken, isAuthenticated, authChecking, logout } = useAuth();
  const [storedTheme, setStoredTheme] = useState(getInitialTheme);
  const [themeLoading, setThemeLoading] = useState(false);

  const dashboardTheme = useMemo(() => buildTheme(storedTheme), [storedTheme]);

  const applyTheme = (theme) => {
    const normalized = normalizeTheme(theme);
    saveTheme(normalized);
    setStoredTheme(normalized);
    return normalized;
  };

  const loadDashboardTheme = async () => {
    const token = accessToken || localStorage.getItem("accessToken");

    if (!token) {
      removeTheme();
      setStoredTheme(defaultTheme);
      return;
    }

    try {
      setThemeLoading(true);

      const response = await fetch("/api/preferences/dashboard-theme/", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (response.status === 401) {
        removeTheme();
        logout();
        return;
      }

      if (response.ok) {
        applyTheme(data);
      }
    } catch {
      const cachedTheme = getInitialTheme();
      setStoredTheme(cachedTheme);
    } finally {
      setThemeLoading(false);
    }
  };

  const updateDashboardTheme = async (payload) => {
    const token = accessToken || localStorage.getItem("accessToken");

    if (!token) return { ok: false };

    const response = await fetch("/api/preferences/dashboard-theme/update/", {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    if (response.status === 401) {
      removeTheme();
      logout();
      return { ok: false, data };
    }

    if (response.ok) {
      applyTheme(data);
      return { ok: true, data };
    }

    return { ok: false, data };
  };

  const toggleThemeMode = async () => {
    const nextMode = storedTheme.theme_mode === "dark" ? "light" : "dark";
    const nextTheme = applyTheme({
      ...storedTheme,
      theme_mode: nextMode,
    });

    const result = await updateDashboardTheme({
      theme_mode: nextMode,
    });

    if (!result.ok) {
      applyTheme(storedTheme);
    }

    return {
      ...result,
      theme: nextTheme,
    };
  };

  const resetDashboardTheme = async () => {
    const token = accessToken || localStorage.getItem("accessToken");

    if (!token) return { ok: false };

    const response = await fetch("/api/preferences/dashboard-theme/reset/", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const data = await response.json();

    if (response.status === 401) {
      removeTheme();
      logout();
      return { ok: false, data };
    }

    if (response.ok) {
      applyTheme(data);
      return { ok: true, data };
    }

    return { ok: false, data };
  };

  useEffect(() => {
    if (authChecking) return;

    if (isAuthenticated) {
      loadDashboardTheme();
      return;
    }

    removeTheme();
    setStoredTheme(defaultTheme);
  }, [authChecking, isAuthenticated, accessToken]);

  const value = useMemo(
    () => ({
      dashboardTheme,
      storedTheme,
      themeLoading,
      loadDashboardTheme,
      updateDashboardTheme,
      resetDashboardTheme,
      toggleThemeMode,
    }),
    [dashboardTheme, storedTheme, themeLoading]
  );

  return (
    <DashboardThemeContext.Provider value={value}>
      {children}
    </DashboardThemeContext.Provider>
  );
};

export const useDashboardTheme = () => useContext(DashboardThemeContext);